import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, Edit, Trash2, Users, Activity, Eye } from "lucide-react";

const EMPTY_FORM = { name: "", age: "", gender: "Male", phone: "", email: "", address: "", blood_group: "" };

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

export default function Patients() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showVitals, setShowVitals] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [vitals, setVitals] = useState({ bp: "", weight: "", temperature: "", pulse: "", spo2: "" });
  const [saving, setSaving] = useState(false);
  const [patientAppts, setPatientAppts] = useState([]);

  const fetchPatients = useCallback(async (q = "") => {
    try {
      const { data } = await api.get(`/patients${q ? `?search=${q}` : ""}`);
      setPatients(data);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  useEffect(() => {
    const timer = setTimeout(() => fetchPatients(search), 300);
    return () => clearTimeout(timer);
  }, [fetchPatients, search]);

  const openAdd = () => { setForm(EMPTY_FORM); setEditPatient(null); setShowModal(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditPatient(p); setShowModal(true); };

  const openVitals = async (p) => {
    setSelectedPatient(p);
    setVitals(p.vitals || {});
    // Load appointments
    try {
      const { data } = await api.get(`/patients/${p.id}/appointments`);
      setPatientAppts(data.data);
    } catch {}
    setShowVitals(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editPatient) {
        await api.put(`/patients/${editPatient.id}`, form);
        toast({ title: "Patient updated" });
      } else {
        await api.post("/patients", { ...form, age: parseInt(form.age) });
        toast({ title: "Patient registered successfully" });
      }
      setShowModal(false);
      fetchPatients(search);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this patient?")) return;
    try {
      await api.delete(`/patients/${id}`);
      toast({ title: "Patient deleted" });
      fetchPatients(search);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const saveVitals = async () => {
    try {
      const vitalsData = {};
      if (vitals.bp) vitalsData.bp = vitals.bp;
      if (vitals.weight) vitalsData.weight = parseFloat(vitals.weight);
      if (vitals.temperature) vitalsData.temperature = parseFloat(vitals.temperature);
      if (vitals.pulse) vitalsData.pulse = parseInt(vitals.pulse);
      if (vitals.spo2) vitalsData.spo2 = parseFloat(vitals.spo2);
      await api.put(`/patients/${selectedPatient.id}/vitals`, vitalsData);
      toast({ title: "Vitals updated" });
      setShowVitals(false);
      fetchPatients(search);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const statusColors = {
    waiting: "bg-amber-100 text-amber-800", in_consultation: "bg-blue-100 text-blue-800",
    completed: "bg-emerald-100 text-emerald-800", cancelled: "bg-rose-100 text-rose-800",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("patients")}</h1>
          <p className="text-slate-500 text-sm mt-1">Register and manage patients</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={openAdd} data-testid="add-patient-btn">
          <Plus size={16} /> Register Patient
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Patients", value: patients.length, color: "text-slate-900" },
          { label: "Male", value: patients.filter(p => p.gender === "Male").length, color: "text-blue-700" },
          { label: "Female", value: patients.filter(p => p.gender === "Female").length, color: "text-rose-600" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className={`text-2xl font-bold font-sans ${s.color}`}>{s.value}</p>
            <p className="text-xs text-slate-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name, phone, or ID..." className="pl-9 border-slate-200" value={search} onChange={e => setSearch(e.target.value)} data-testid="patient-search-input" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Patient ID</TableHead>
              <TableHead className="font-semibold text-slate-700">Name</TableHead>
              <TableHead className="font-semibold text-slate-700">Age / Gender</TableHead>
              <TableHead className="font-semibold text-slate-700">Phone</TableHead>
              <TableHead className="font-semibold text-slate-700">Blood Group</TableHead>
              <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">{t("loading")}</TableCell></TableRow>
            ) : patients.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                <Users size={32} className="mx-auto mb-2 opacity-30" />
                <p>No patients found</p>
              </TableCell></TableRow>
            ) : patients.map(p => (
              <TableRow key={p.id} className="table-row-hover" data-testid={`patient-row-${p.id}`}>
                <TableCell>
                  <Badge variant="outline" className="font-mono text-xs border-emerald-200 text-emerald-700">{p.patient_id}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-sky-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-sky-800">{p.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{p.name}</p>
                      {p.email && <p className="text-xs text-slate-500">{p.email}</p>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-700">{p.age} / {p.gender}</TableCell>
                <TableCell className="text-sm text-slate-600">{p.phone}</TableCell>
                <TableCell>
                  {p.blood_group && <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-xs">{p.blood_group}</Badge>}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600" onClick={() => openVitals(p)} data-testid={`vitals-btn-${p.id}`}>
                      <Activity size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900" onClick={() => openEdit(p)} data-testid={`edit-patient-${p.id}`}>
                      <Edit size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDelete(p.id)} data-testid={`delete-patient-${p.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-sans">{editPatient ? "Edit Patient" : "Register New Patient"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-sm text-slate-700">Full Name</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 border-slate-200" required data-testid="patient-name-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Age</Label>
                <Input type="number" value={form.age} onChange={e => setForm({...form, age: e.target.value})} className="mt-1 border-slate-200" required data-testid="patient-age-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Gender</Label>
                <select value={form.gender} onChange={e => setForm({...form, gender: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="patient-gender-select">
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 border-slate-200" required data-testid="patient-phone-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 border-slate-200" data-testid="patient-email-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Blood Group</Label>
                <select value={form.blood_group} onChange={e => setForm({...form, blood_group: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="">Select</option>
                  {BLOOD_GROUPS.map(bg => <option key={bg}>{bg}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <Label className="text-sm text-slate-700">Address</Label>
                <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="mt-1 border-slate-200" data-testid="patient-address-input" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-patient-btn">
                {saving ? "Saving..." : editPatient ? "Update" : "Register"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vitals + History Modal */}
      <Dialog open={showVitals} onOpenChange={setShowVitals}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-sans">{selectedPatient?.name} — Details</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="vitals">
            <TabsList className="mb-4">
              <TabsTrigger value="vitals">Vitals</TabsTrigger>
              <TabsTrigger value="history">Visit History</TabsTrigger>
            </TabsList>
            <TabsContent value="vitals">
              <div className="grid grid-cols-3 gap-4 mb-4">
                {[
                  { label: "BP (mmHg)", key: "bp", placeholder: "120/80" },
                  { label: "Weight (kg)", key: "weight", placeholder: "70" },
                  { label: "Temp (°F)", key: "temperature", placeholder: "98.6" },
                  { label: "Pulse (bpm)", key: "pulse", placeholder: "72" },
                  { label: "SpO2 (%)", key: "spo2", placeholder: "98" },
                ].map(({ label, key, placeholder }) => (
                  <div key={key}>
                    <Label className="text-sm text-slate-700">{label}</Label>
                    <Input value={vitals[key] || ""} onChange={e => setVitals({...vitals, [key]: e.target.value})} placeholder={placeholder} className="mt-1 border-slate-200" data-testid={`vitals-${key}`} />
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={saveVitals} data-testid="save-vitals-btn">Save Vitals</Button>
              </div>
            </TabsContent>
            <TabsContent value="history">
              {patientAppts.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No visit history</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {patientAppts.map(a => (
                    <div key={a.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{formatDoctorName(a.doctor_name)}</p>
                        <p className="text-xs text-slate-500">{a.complaint || "General Visit"}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">{a.visit_date?.split("T")[0]}</p>
                        <Badge className={`text-xs ${a.status === "completed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{a.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
